import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import { Loader, Tiles, Theme, Text, Button, Icons, FilterClaimsModal, ContainerControlSite, Content, Files } from '@simplybenefits/sb-ui-library';

import * as dates from '@simplybenefits/sb-dates';
import Header from '../../components/Header.js';
import { getUser, getClaims } from '../../actions/users.js';
import Claims from './Claims.js';
import Dependents from './Dependents.js';
import Beneficiaries from './Beneficiaries.js';
import Trustee from './Trustee.js';
import Usage from './Usage/index.js';
import Coverage from './Coverage/index.js';
import SpendingAccounts from './SpendingAccounts/index.js';
import Profile from './Profile/index.js';
import Invoices from './Invoices/index.js';
import Marketplace from './Marketplace/index.js';
import CoverageHistory from './Coverage/CoverageHistory/index.js';
import Notifications from '../Notifications/NotificationsList.js';
import permissionLevel from '../../services/permissions.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import CarryForward from '../CarryForward/CarryForward.js';
import AccountNotesForMember from './AccountNotesForMember.js';
import ChangeRequestsList from '../Administration/components/ChangeRequestsList.js';
import { constants } from '@simplybenefits/sb-utils';
import {
    listFiles,
    createFolder,
    uploadFile,
    downloadFile,
    removeFile,
    makePrivate,
    makePublic
} from '../../actions/files.js';

class UserDashboard extends PureComponent {
    state = {
        filterClaimsDialogOpen: false,
        activeFilters: {},
        amountOfActiveFilters: 0,
        selectedSection: null
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Members', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    componentDidMount() {
        this.props.dispatch(getUser(this.props.match.params.userID));

        if (window.location.hash && window.location.hash !== '#dashboard') {
            this.setState({
                selectedSection: window.location.hash.replace('#', '')
            });
        }
    }

    _selectSection = (section) => {
        if (section) {
            window.location.hash = section;
        } else {
            window.location.hash = 'dashboard';
        }

        this.setState({
            selectedSection: section
        });
    };

    _buildBreadcrumbs = () => {
        if (!this.state.selectedSection) {
            return [
                {
                    title: 'Members',
                    link: `/members`
                }
            ];
        }

        return [
            {
                title: 'Members',
                link: `/members`
            },
            {
                title: `${ this.props.currentUser.meta.firstname } ${ this.props.currentUser.meta.lastname }`,
                onClick: () => this._selectSection(null)
            }
        ];
    };

    _getSubHeader = () => {
        if (this.state.selectedSection === 'user-profile') {
            return 'Profile';
        } else if (this.state.selectedSection === 'user-claims') {
            return 'Claims';
        } else if (this.state.selectedSection === 'user-dependents') {
            return 'Dependents';
        } else if (this.state.selectedSection === 'user-beneficiaries') {
            return 'Beneficiaries';
        } else if (this.state.selectedSection === 'user-trustee') {
            return 'Trustee';
        } else if (this.state.selectedSection === 'user-usage') {
            return 'Usage';
        } else if (this.state.selectedSection === 'user-coverage') {
            return 'Coverage';
        } else if (this.state.selectedSection === 'user-notifications') {
            return 'Notifications';
        } else if (this.state.selectedSection === 'spending-accounts') {
            return 'Spending Accounts';
        } else if (this.state.selectedSection === 'invoices') {
            return 'Invoices';
        } else if (this.state.selectedSection === 'marketplace') {
            return 'Marketplace';
        } else if (this.state.selectedSection === 'coverageHistory') {
            return "Coverage History";
        } else if (this.state.selectedSection === 'user-carry-forward') {
            return "Carry Forward";
        } else if (this.state.selectedSection === 'files') {
            return "Files";
        } else if (this.state.selectedSection === 'requested-changes') {
            return "Requested Changes";
        }

        return `${ this.props.currentUser.meta.firstname } ${ this.props.currentUser.meta.lastname }`;
    };

    _applyFilters = (filters, sort) => {
        this.props.dispatch(getClaims(this.props.match.params.userID, filters, sort, 0));

        // Count all filters that are not their default value
        let amountOfActiveFilters = 0;

        amountOfActiveFilters += filters.patient === 'All Patients' ? 0 : 1;
        amountOfActiveFilters += filters.category === 'All Categories' ? 0 : 1;
        amountOfActiveFilters += filters.service === 'All Service Types' ? 0 : 1;
        amountOfActiveFilters += filters.year === 'All Years' ? 0 : 1;
        amountOfActiveFilters += filters.status === 'All Statuses' ? 0 : 1;
        amountOfActiveFilters += !sort.sortBy ? 0 : 1;

        // set active filters to save for later
        this.setState({
            activeFilters: filters,
            activeSort: sort,
            amountOfActiveFilters
        });

        this._toggleFiltersModal(false);
    };

    _resetFilters = () => {
        this.props.dispatch(getClaims(this.props.currentUser.meta.userID, {}, false, 0));

        this.setState({
            activeFilters: {},
            activeSort: false,
            amountOfActiveFilters: 0
        });

        this._toggleFiltersModal(false);
    };

    _loadMore = () => {
        this.props.dispatch(getClaims(this.props.currentUser.meta.userID, this.state.activeFilters, this.state.activeSort, this.props.cursor));
    };

    _toggleFiltersModal = (value = null) => {
        this.setState({
            filterClaimsDialogOpen: value === null ? !this.state.filterClaimsDialogOpen : value
        });
    };

    render() {
        const { currentUser, accessType } = this.props;
        const isSpendingAccountOnly = currentUser?.meta?.isSpendingAccountOnly;
        const hasStandardClaims = currentUser?.claims?.length >= 1;
        const showClaims = permissionLevel('Claims', accessType).includes('read') &&
            (!isSpendingAccountOnly || (isSpendingAccountOnly && hasStandardClaims));

        if (!currentUser) {
            return <Loader isContainer/>;
        }

        const subHeader = this._getSubHeader();
        const breadcrumbs = this._buildBreadcrumbs();

        let tiles = [];

        showClaims &&
            tiles.push({
                title: 'Claims',
                description: 'View and validate user claims.',
                onClick: () => this._selectSection('user-claims'),
                testid: 'user-claims-tile'
            });

        permissionLevel('Profile', accessType).includes('read') &&
            tiles.push({
                title: 'Profile',
                description: 'View and edit user profile.',
                onClick: () => this._selectSection('user-profile'),
                testid: 'user-profile-tile'
            });

        permissionLevel('Dependents', accessType).includes('read') &&
            tiles.push({
                title: 'Dependents',
                description: 'View user dependents.',
                onClick: () => this._selectSection('user-dependents'),
                testid: 'user-dependents-tile'
            });

        permissionLevel('Beneficiaries', accessType).includes('read') &&
            tiles.push({
                title: 'Beneficiaries',
                description: 'View user beneficiaries.',
                onClick: () => this._selectSection('user-beneficiaries'),
                testid: 'user-beneficiaries-tile'
            });

        permissionLevel('Trustee', accessType).includes('read') &&
            tiles.push({
                title: 'Trustee',
                description: 'View users trustee.',
                onClick: () => this._selectSection('user-trustee'),
                testid: 'user-trustee-tile'
            });

        permissionLevel('Usage', accessType).includes('read') && !isSpendingAccountOnly && 
            tiles.push({
                title: 'Usage',
                description: 'View users coverage usage and remaining balances',
                onClick: () => this._selectSection('user-usage'),
                testid: 'user-usage-tile'
            });

        permissionLevel('Coverage', accessType).includes('read') && !isSpendingAccountOnly && 
            tiles.push({
                title: 'Coverage',
                description: 'View users plan coverages',
                onClick: () => this._selectSection('user-coverage'),
                testid: 'user-coverage-tile'
            });

        permissionLevel('Coverage', accessType).includes('read') &&
            tiles.push({
                title: 'Spending Accounts',
                description: 'See your health and lifestyle account balances',
                onClick: () => this._selectSection('spending-accounts'),
                testid: 'user-spending-accounts-tile'
            });

        if (currentUser.invoices.length > 0) {
            permissionLevel('Invoices', accessType).includes('read') &&
                tiles.push({
                    title: 'Invoices',
                    description: 'View members invoice history.',
                    onClick: () => this._selectSection('invoices'),
                    testid: 'user-invoices-tile'
                });
        }

        permissionLevel('Invoices', accessType).includes('read') &&
            tiles.push({
                title: 'Marketplace',
                description: 'View and manage member Marketplace products.',
                onClick: () => this._selectSection('marketplace'),
                testid: 'user-marketplace-tile'
            });

        permissionLevel('Coverage History', accessType).includes('read') &&
            tiles.push({
                title: 'Coverage History',
                description: 'View this members coverage history records',
                onClick: () => this._selectSection('coverageHistory'),
                testid: 'user-coverageHistory-tile'
            });

        if (currentUser.invoices.length > 0) {
            permissionLevel('Invoices', accessType).includes('read') &&
                tiles.push({
                    title: 'Member Carry Forwards',
                    description: "View member's pending carry forwards.",
                    onClick: () => this._selectSection('user-carry-forward'),
                    testid: 'user-carry-forward-tile',
                    icon: 'Carry Forward'
                });
        }

        permissionLevel('Member Files', accessType).includes('read') &&
            tiles.push({
                title: 'Files',
                description: 'View and manage member files.',
                onClick: () => this._selectSection('files'),
                testid: 'user-files-tile'
            });

        permissionLevel('Administration', accessType).includes('read') &&
            tiles.push({
                title: 'Requested Changes',
                description: "View this member's Requested Changes.",
                onClick: () => this._selectSection('requested-changes'),
                testid: 'user-requested-changes'
            });

        const status = constants.memberStatusMap[currentUser.meta.status];
        let statusColor = Theme.colors.brandGreen;

        if (!currentUser.meta.benefitsActive || currentUser.meta.benefitsActive === 0) {
            statusColor = Theme.colors.red;
        }

        return (
            <ContainerControlSite background={"true"}>
                <Header
                    subHeader={ subHeader }
                    breadcrumbs={breadcrumbs}
                    selectedTitle="members"
                />

                <Content>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {/* Only show claims filter button when we are on the claims page (obviously :D) */}
                        {
                            this.state.selectedSection === 'user-claims' &&
                            (currentUser.claims.length > 0 || this.state.amountOfActiveFilters > 0) &&
                                <PermissionsComponent
                                    permission="Claims"
                                    accessType={this.props.accessType}
                                    level="read"
                                >
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            color={ this.state.amountOfActiveFilters > 0 ? 'primary' : 'white' }
                                            noHover
                                            icon="filter-outline"
                                            onClick={ () => this._toggleFiltersModal() }
                                            id="filterclaims"
                                            customStyle={{
                                                textTransform: "capitalize",
                                                fontSize: 18
                                            }}
                                        >
                                            <Icon
                                                style={{ marginRight: 8, color: 'inherit' }}
                                            >
                                                { Icons.Filter }
                                            </Icon>

                                            {
                                                this.state.amountOfActiveFilters > 0 ?
                                                    `${ this.state.amountOfActiveFilters } ${ this.state.amountOfActiveFilters === 1 ? 'Filter' : 'Filters' } Applied`
                                                    :
                                                    'Filter'
                                            }
                                        </Button>
                                    </div>
                                </PermissionsComponent>
                        }

                        <div style={{ flex: 1 }}>
                            <a href={`/accounts/${ currentUser.meta.accountID }`} style={{ textDecoration: 'none' }}>
                                <Text
                                    align="right"
                                    light
                                    customStyle={{ margin: '0 auto' }}
                                >
                                    { currentUser.meta.company } ({ currentUser.meta.groupNum })
                                </Text>
                            </a>

                            <Text
                                align="right"
                                light
                                customStyle={{ margin: '0 auto' }}
                            >
                                Certificate #: {currentUser.meta.cert}
                            </Text>

                            {
                                currentUser.meta.isStudentGroup ?
                                    <Text
                                        align="right"
                                        light
                                        customStyle={{ margin: '0 auto' }}
                                    >
                                        Student
                                    </Text>
                                    :
                                    null
                            }

                            {
                                currentUser.meta.useAutoBooklet === 1 &&
                                    <Text
                                        align="right"
                                        bold
                                        customStyle={{ margin: '0 auto' }}
                                    >
                                        Member has automated booklets enabled
                                    </Text>
                            }

                            {
                                currentUser.meta.activeStatusRecord &&
                                    <Text
                                        align="right"
                                        light
                                        customStyle={{ margin: '0 auto' }}
                                    >
                                        { // Survivor Benefits/Severance
                                            [ 11, 12 ].includes(currentUser.meta.status) ?
                                                `Pending Termination Date: ${ dates.getLocalDisplay(currentUser.meta.activeStatusRecord.dateToTerminate) }`
                                                :
                                                `Expected Return Date: ${ dates.getLocalDisplay(currentUser.meta.activeStatusRecord.returnDate) }`
                                        }
                                    </Text>
                            }

                            {
                                [ 'PENDING', 'ACTIVE', 'TERMINATED' ].includes(status.toUpperCase()) ?
                                    <Text
                                        mediumLarge
                                        bold
                                        color={statusColor}
                                        margin="0 5px 0 0"
                                        align="right"
                                        customStyle={{ margin: '0 auto 10px  auto' }}
                                    >
                                        { status }
                                    </Text>
                                    :
                                    <Text
                                        mediumLarge
                                        bold
                                        color={statusColor}
                                        margin="0 5px 0 0"
                                        align="right"
                                        customStyle={{ margin: '0 auto 10px  auto' }}
                                    >
                                        {`${ status } - ${ !currentUser.meta.benefitsActive || currentUser.meta.benefitsActive === 0 ? 'Inactive' : 'Active' }`}
                                    </Text>
                            }
                        </div>
                    </div>

                    <AccountNotesForMember
                        accountID={ currentUser.meta.accountID }
                        classID={ currentUser.meta.classID }
                        divisionID={ currentUser.meta.divisionID }
                    />

                    {
                        this.state.selectedSection === null &&
                            <Tiles tiles={tiles} />
                    }

                    {
                        this.state.selectedSection === 'user-claims' &&
                            <PermissionsComponent
                                permission="Claims"
                                accessType={ this.props.accessType }
                                level="read"
                            >
                                <Claims
                                    claims={ currentUser.claims }
                                    meta={ currentUser.meta }
                                    amountOfActiveFilters={ this.state.amountOfActiveFilters }
                                    resetFilters={ this._resetFilters }
                                    loadMore={ this._loadMore }
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'user-dependents' &&
                            <PermissionsComponent
                                permission="Dependents"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Dependents
                                    dependents={currentUser.dependents}
                                    meta={currentUser.meta}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'user-beneficiaries' &&
                            <PermissionsComponent
                                permission="Beneficiaries"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Beneficiaries
                                    beneficiaries={currentUser.beneficiaries}
                                    meta={currentUser.meta}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'user-trustee' &&
                            <PermissionsComponent
                                permission="Trustee"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Trustee
                                    trustee={currentUser.trustee}
                                    meta={currentUser.meta}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'user-usage' &&
                            <PermissionsComponent
                                permission="Usage"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Usage
                                    usage={currentUser.usage}
                                    coverage={currentUser.coverage}
                                    meta={currentUser.meta}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'user-coverage' &&
                            <PermissionsComponent
                                permission="Coverage"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Coverage
                                    coverage={currentUser.coverage}
                                    lsaClaimTypes={currentUser.spending.lsaClaimTypes}
                                    meta={currentUser.meta}
                                    planDesign={ currentUser.planDesign }
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'spending-accounts' &&
                            <SpendingAccounts
                                coverage={currentUser.coverage}
                                healthTotal={currentUser.spending.health.total}
                                healthRemaining={currentUser.spending.health.remaining}
                                lifestyleTotal={currentUser.spending.lifestyle.total}
                                lifestyleRemaining={currentUser.spending.lifestyle.remaining}
                                lsaClaimTypes={ currentUser.spending.lsaClaimTypes }
                                userID={currentUser.meta.userID}
                                lifestyleRollover={currentUser.spending.lifestyle.rollover}
                                healthRollover={currentUser.spending.health.rollover}
                            />
                    }

                    {
                        this.state.selectedSection === 'invoices' &&
                            <Invoices meta={currentUser.meta} />
                    }

                    {
                        this.state.selectedSection === 'user-notifications' &&
                            <Notifications
                                notifications={currentUser.notifications}
                                meta={currentUser.meta}
                                notificationsType="user"
                            />
                    }

                    {
                        this.state.selectedSection === 'user-profile' &&
                            <PermissionsComponent
                                permission="Profile"
                                accessType={this.props.accessType}
                                level="read"
                            >
                                <Profile
                                    meta={currentUser.meta}
                                    goBack={this._selectSection}
                                    employee={this.props.currentUser}
                                />
                            </PermissionsComponent>
                    }

                    {
                        this.state.filterClaimsDialogOpen &&
                            <FilterClaimsModal
                                applyFilters={ this._applyFilters }
                                close={ () => this._toggleFiltersModal() }
                                resetFilters={ this._resetFilters }
                                activeFilters={ this.state.activeFilters }
                                activeSort={ this.state.activeSort }
                                user={ this.props.currentUser.meta }
                                dependents={ this.props.currentUser.dependents }
                                hsaClaimTypes={ this.props.hsaClaimTypes }
                                isMasterSite
                                loader={ this.props.loader }
                            />
                    }

                    {
                        this.state.selectedSection === 'marketplace' &&
                            <Marketplace
                                meta={currentUser.meta}
                                goBack={this._selectSection}
                            />
                    }

                    {
                        this.state.selectedSection === 'coverageHistory' &&
                            <CoverageHistory
                                currentUser={ currentUser }
                                goBack={ this._selectSection }
                            />
                    }

                    {
                        this.state.selectedSection === 'user-carry-forward' &&
                            <CarryForward
                                userID={currentUser.meta.userID}
                                isDivision={false}
                            />
                    }

                    {
                        this.state.selectedSection === 'files' &&
                            <Files
                                isMasterSite={ true }
                                type="users"
                                id={ currentUser.meta.userID }
                                parentFolder="files"
                                dispatch={ this.props.dispatch }
                                listFiles={ listFiles }
                                createFolder={ createFolder }
                                uploadFile={ uploadFile }
                                downloadFile={ downloadFile }
                                removeFile={ removeFile }
                                makePrivate={ makePrivate }
                                makePublic={ makePublic }
                                loader={ this.props.loader }
                            />
                    }

                    {
                        this.state.selectedSection === 'requested-changes'
                            ? (
                                <ChangeRequestsList
                                    userID={currentUser.meta.userID}
                                    isMemberChangeRequestList={true}
                                />
                            )
                            : null
                    }
                </Content>

                <span id="users-single" />
            </ContainerControlSite>
        );
    }
}

UserDashboard.propTypes = {
    currentUser: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentUser: state.users.currentUser,
    accessType: state.master.access,
    hsaClaimTypes: state.master.claimTypes,
    cursor: state.users.currentUser?.claimsCursor
});

export default (connect(mapStateToProps)(UserDashboard));